import React, { useMemo } from 'react';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import parse from 'html-react-parser';
import DOMPurify from 'dompurify';
import { isDesktop } from 'react-device-detect';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import DateTimeIcon from 'assets/images/calendar/icon-datetime.svg';
import LocationIcon from 'assets/images/calendar/icon-location.svg';
import NotesIcon from 'assets/images/calendar/icon-notes.svg';
import StatusIcon from 'assets/images/calendar/icon-status.svg';
import VideoCallIcon from 'assets/images/calendar/icon-video-call.svg';
import GuestCountIcon from 'assets/images/calendar/icon-guest-count.svg';
import GuestAttendingIcon from 'assets/images/calendar/icon-guest-attending.svg';
import GuestNotAttendingIcon from 'assets/images/calendar/icon-guest-not-attending.svg';
import GuestMaybeAttendingIcon from 'assets/images/calendar/icon-guest-maybe-attending.svg';
import GuestNoReplyIcon from 'assets/images/calendar/icon-guest-no-reply.svg';
import { EventDateTimeDisplay } from './EventDateTimeDisplay';

dayjs.extend(timezone);

const ExternalEventCard = ({ eventDetails, userDetails }) => {
  const { title, startStr, endStr, extendedProps } = eventDetails || {};
  const {
    whenType,
    conferencingUrl,
    busy,
    organizer,
    participants = [],
    location,
    notes = '',
    externalCalendarId,
    htmlLink,
    startTz: displayTz,
  } = extendedProps || {};
  const { timeZone, timeFormat, loginCalendarsById = {}, integration } = userDetails || {};
  const { provider } = integration || {};
  const maxVisibleParticipants = 5;
  const start = dayjs(startStr).tz(timeZone);
  const end = dayjs(endStr).tz(timeZone);
  const sameDay = useMemo(() => {
    if (whenType === 'timespan') return start.isSame(end, 'day');
    // For all day events, end time is the next day but at 00:00:00, so subtract
    // 1 day to see if it's the same day.
    return end.subtract(1, 'day').isSame(start, 'day');
  }, [whenType]);
  const calendarName = loginCalendarsById[externalCalendarId]?.name || '';

  // #TODO: Events from Microsoft can have a description built by a fully featured wysiwyg.
  // Will need to likely strip out some tags so the description doesn't get blown up in the app.
  // Depending on the source, the notes/description of an event can be a html string.
  const sanitizedNotes = parse(
    DOMPurify.sanitize(
      notes,
      {
        FORBID_ATTR: ['style'],
        FORBID_TAGS: ['img', 'table'],
      },
    ),
    { transform: transformExternalLinks },
  );

  const normalizeRsvpStatus = (status) => (status || '').toLowerCase();

  const getRsvpStatusIcon = (status) => {
    switch (normalizeRsvpStatus(status)) {
      case 'yes':
        return GuestAttendingIcon;
      case 'no':
        return GuestNotAttendingIcon;
      case 'maybe':
        return GuestMaybeAttendingIcon;
      default:
        return GuestNoReplyIcon;
    }
  };

  const getRsvpStatusPriority = (status) => {
    switch (normalizeRsvpStatus(status)) {
      case 'yes':
        return 1;
      case 'no':
        return 2;
      case 'maybe':
        return 3;
      default:
        return 4;
    }
  };

const sortParticipants = (participantList, organizerEmail) => {
  return [...participantList].sort((a, b) => {
    if (organizerEmail) {
      // Always place the organizer at the top
      if (a.email === organizerEmail) return -1;
      if (b.email === organizerEmail) return 1;
    }
    // Then sort by RSVP status priority
    return getRsvpStatusPriority(a.status) - getRsvpStatusPriority(b.status);
  });
};

const normalizedParticipants = useMemo(() => {
  if (!organizer?.email) {
    return sortParticipants(participants);
  }

  // Add organizer if missing
  const hasOrganizer = participants.some((p) => p.email === organizer.email);

  const normalizedList = hasOrganizer
    ? participants.map((p) =>
        p.email === organizer.email ? { ...p, status: 'yes' } : p,
      )
    : [
        ...participants,
        {
          name: organizer.name || organizer.email || 'Unknown',
          email: organizer.email,
          status: 'yes',
        },
      ];

  return sortParticipants(normalizedList, organizer.email);
}, [participants, organizer]);

  const confirmedGuestCount = normalizedParticipants?.filter((p) => p.status === 'yes')?.length || 0;
  const showParticipantMore = normalizedParticipants.length > maxVisibleParticipants;

  const handleLocationClick = (locationData, desktop) => {
    if (!locationData) return;

    if (desktop) {
      const mapUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(locationData)}`;
      window.open(mapUrl, '_blank');
    } else {
      window.location.href = `maps://?q=${encodeURIComponent(locationData)}`;
    }
  };

  return (
    <div className="event-card info-card event-card--external">
      <div className="info-card__content">
        <div className="info-card__row">
          <div className="info-card__title ap-text">
            {title}
          </div>
        </div>

        <div className="info-card__row">
          <div className="info-card__icon-wrapper">
            <img
              className="info-card__icon"
              src={DateTimeIcon}
              alt="date-time icon"
            />
          </div>
          <div>
            <EventDateTimeDisplay
              start={start}
              end={end}
              whenType={whenType}
              timeFormat={timeFormat}
              sameDay={sameDay}
              displayTz={displayTz}
              originalOffset={start.tz(displayTz).utcOffset()}
            />
          </div>
        </div>

        <div className="info-card__row">
          <div className="info-card__icon-wrapper">
            <img
              className="info-card__icon"
              src={VideoCallIcon}
              alt="date-time icon"
            />
          </div>
          <div>
            {conferencingUrl ? (
              <a
                href={conferencingUrl}
                target="_blank"
                rel="noreferrer nofollow"
                className="ap-text--sm gold"
              >
                Join video call
              </a>
            ) : (
              '-'
            )}
          </div>
        </div>
        <div className="info-card__row">
          <div className="info-card__icon-wrapper">
            <img
              className="info-card__icon"
              src={GuestCountIcon}
              alt="guest count icon"
            />
          </div>
          <div>{`${confirmedGuestCount} Guest${
            confirmedGuestCount === 1 ? '' : 's'
          } Confirmed`}</div>
        </div>
        <div className="info-card__participants-wrapper">
          {normalizedParticipants.slice(0, maxVisibleParticipants).map((participant, index) => {
            const participantName = participant?.name || participant?.email || 'Unknown Guest';
            const participantIcon = getRsvpStatusIcon(participant?.status);
            const isOrganizer = participant?.email === organizer?.email;
            return (
              <div className="info-card__row"
                key={index}
              >
                <div className="info-card__participant">
                  <div className="info-card__icon-wrapper">
                    <img
                      className="info-card__icon"
                      src={participantIcon}
                      alt="participant rsvp status icon"
                    />
                  </div>
                  <span
                    className="info-card__participant-name"
                    title={participantName}
                  >
                    {participantName}
                  </span>
                  {isOrganizer && (
                    <span className="info-card__participant-organizer">
                      &nbsp;(Organizer)
                    </span>
                  )}
                </div>
              </div>
            );
          })}
          {showParticipantMore && (
            <div className="info-card__participants-more">
              {htmlLink ? (
                <a
                  href={htmlLink}
                  target="_blank"
                  rel="noreferrer nofollow"
                  className="ap-text--sm gold"
                >
                  View All
                </a>
              ) :'…'}
            </div>
          )}
        </div>

        <div className="info-card__row">
          <div className="info-card__icon-wrapper">
            <img
              className="info-card__icon"
              src={StatusIcon}
              alt="date-time icon"
            />
          </div>
          <div>
            {busy ? 'Busy' : 'Free'}
          </div>
        </div>

        <div className="info-card__row"
          style={{ ... location && { position: 'relative' } }}
        >
          <div className="info-card__icon-wrapper">
            <img
              className="info-card__icon"
              src={LocationIcon}
              alt="date-time icon"
            />
          </div>
          <div
            onClick={() => handleLocationClick(location, isDesktop)}
            style={{ ... location && { cursor: 'pointer' } }}
          >
            {location || '-'}
            {location && (
              <FontAwesomeIcon
                icon={['fa', 'angle-right']}
                className="click-indicator"
              />
            )}
          </div>
        </div>

        <div className="info-card__row">
          <div className="info-card__icon-wrapper">
            <img
              className="info-card__icon"
              src={NotesIcon}
              alt="date-time icon"
            />
          </div>
          <div className="info-card__description">
            {notes ? sanitizedNotes : '-'}
          </div>
        </div>

        <div className="info-card__row">
          <div className="info-card__icon-wrapper">
            <img
              className="info-card__icon"
              src={DateTimeIcon}
              alt="date-time icon"
            />
          </div>
          <div>
            {calendarName}
          </div>
        </div>
      </div>

      {htmlLink && (
        <a
          href={htmlLink}
          className="button external-event-card__button"
          target="_blank"
          rel="noreferrer nofollow"
        >
          View in {provider || 'External Calendar'}
        </a>
      )}

    </div>
  );
};

/* eslint-disable jsx-a11y/anchor-has-content */
/* eslint-disable-next-line no-unused-vars */
const transformExternalLinks = (reactNode, domNode, index) => {
  const { props, type } = reactNode;
  if (type === 'a') {
    return (
      <a
        {...props}
        target="_blank"
        rel="noopener noreferrer"
      />
    );
  }
  return reactNode;
};
/* eslint-enable jsx-a11y/anchor-has-content */

export default ExternalEventCard;
